import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { UserService } from 'app/services/user.service';
import { ContactService } from 'app/services/contact.service';
import { DriverLicenceModel } from 'app/models/dms/driverLicence.model';
import { HttpEventType } from '@angular/common/http';
import { OpportunityService } from 'app/services/opportunity.service';
import { Component, OnInit } from '@angular/core';
import { Platform, AlertController, ModalController } from '@ionic/angular';
import { ViewChild, ElementRef } from '@angular/core';
import '@ionic/pwa-elements';
import { Constants } from 'app/utility/constants';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { DateHelper } from 'app/utility/datehelper';
import { Subscription } from 'rxjs';
import { loadImageElement, normalizeImageFileToDataUrl } from 'app/utility/image-heic.util';

@Component({
	selector: 'app-licence-scanner',
	templateUrl: './licence-scanner.component.html',
	styleUrls: ['./licence-scanner.component.scss'],
	standalone: false
})
export class LicenceScannerComponent implements OnInit
{

	cameraLaunching: boolean = true;
	cameraMode: boolean = false;

	uploading: boolean = false;

	showTips: boolean = true;

	driverLicenceData: DriverLicenceModel;
	showLicenceData: boolean = false;
	error: boolean = false;

	dismissed: boolean = false;

	isDatePickerShowing: boolean = false;
	dateFormatShort = Constants.DateFormatShort;
	dobFormatted: string = '';
	isPortrait: boolean = false;
	minVersion: boolean = false;
	subscriptions: Subscription[] = [];

	constructor(
		private platform: Platform,
		private opportunityService: OpportunityService,
		private userService: UserService,
		private alertController: AlertController,
		private modalController: ModalController,
		private contactService: ContactService,
		private screenOrientation: ScreenOrientation
	)
	{

	}

	ngOnInit()
	{
		this.isPortrait = this.platform.isPortrait();
		//onresize update the isPortrait variable
		let resizeSubscription = this.platform.resize.subscribe(() =>
		{
			this.isPortrait = this.platform.isPortrait();
		});
		this.subscriptions.push(resizeSubscription);

		this.minVersion = this.userService.mcMinVersion("3.1.33");
		if (localStorage.getItem(Constants.LocalStorage_LicenceScannerSkipTips) == "1")
		{
			this.takePhoto();
		}
		else
		{
			this.showTips = true;
			this.showTipsTimer(0);
		}
	}

	ngOnDestroy()
	{
		if (this.tipsTimer)
		{
			clearTimeout(this.tipsTimer);
		}
		this.subscriptions.forEach((subscription) =>
		{
			subscription.unsubscribe();
		});
	}


	tipsTimer: number;
	totalTime = 5000;
	public showTipsTimer(count)
	{
		if (!this.cameraLaunching) return;
		count += 100;
		if (count == this.totalTime)
		{
			this.takePhoto();
		}
		this.tipsTimer = count / this.totalTime;
		setTimeout(() =>
		{
			this.showTipsTimer(count);
		}, 100);
	}

	async disableTips()
	{
		localStorage.setItem(Constants.LocalStorage_LicenceScannerSkipTips, "1");
		this.showTips = false;
		this.takePhoto();
	}

	reset()
	{
		this.dismissModal(false, true);
	}

	async takePhoto()
	{
		if (!this.minVersion) return;

		this.cameraLaunching = false;
		this.cameraMode = true;
		this.error = false;
		this.showLicenceData = false;

		if (this.platform.is('hybrid'))
		{
			setTimeout(() =>
			{
				this.takeNativePhoto()
			}, 300);
		}
		else
		{
			setTimeout(() =>
			{
				this.takeHtmlPhoto();
			}, 300);
		}
	}

	photo: string;
	async takeNativePhoto(sourceType: CameraSource = CameraSource.Camera)
	{
		this.photo = "";

		const image = await Camera.getPhoto({
			quality: 100,
			allowEditing: false,
			resultType: CameraResultType.Base64,
			width: 1280,
			height: 960,
			correctOrientation: false,
			source: sourceType,
			saveToGallery: false,
		});
		this.uploadBase64File(image.base64String, false);
	}


	@ViewChild('inputcamera') cameraInput: ElementRef;
	async takeHtmlPhoto()
	{
		this.photo = "";
		const element = this.cameraInput.nativeElement as HTMLInputElement;
		element.onchange = async () =>
		{
			if (!element.files || element.files.length === 0)
			{
				return;
			}

			try
			{
				const source = await normalizeImageFileToDataUrl(element.files[0]);
				const img = await loadImageElement(source);

				const elem = document.createElement('canvas');

				const ctx = elem.getContext('2d');
				const maxWidth: number = 1280;
				const width = maxWidth;
				const height = width;
				var scaledWidth = width;
				var scaledHeight = height;

				var scaleFactor = width / img.width;
				if (img.height > img.width)
				{
					scaleFactor = height / img.height;
					scaledWidth = img.width * scaleFactor;
				}
				else
				{
					scaledHeight = img.height * scaleFactor;
				}


				//resize biggest edge to the "width" dimension (this fixes issues with portrait uploads)

				elem.width = scaledWidth;
				elem.height = scaledHeight;

				ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
				var dataURL = ctx.canvas.toDataURL('image/jpeg', 1);
				// console.log("Image resized");
				this.uploadBase64File(dataURL, true);
			}
			catch (e)
			{
				// console.log("Error reading file", e);
				this.error = true;
				this.cameraLaunching = false;
				this.cameraMode = false;
				this.showLicenceData = false;
			}

			element.value = "";
		};

		this.cameraInput.nativeElement.click();
	}

	dataUrl: string;
	async uploadBase64File(base64image, prefixed: boolean = false)
	{
		// console.log(base64images, prefixed);
		this.uploading = true;

		var base64 = (prefixed ? "" : "data:image/png;base64,") + base64image;
		// console.log("uploadBase64Files, file is: ", base64);
		var url = base64;
		this.dataUrl = url;

		let blob = await fetch(url).then(r => r.blob());

		this.photo = url;
		this.uploadBlob(blob);
	}


	async uploadBlob(blob: Blob)
	{
		this.errorTitle = "";
		this.errorMessage = "";
		await this.opportunityService.scanLicence(blob).subscribe({
			next: (data: any) =>
			{
				// console.log(data);
				if (data.type === HttpEventType.DownloadProgress)
				{
					// {
					// loaded:11, // Number of bytes uploaded or downloaded.
					// total :11 // Total number of bytes to upload or download
					// }
				}
				else if (data.type === HttpEventType.UploadProgress)
				{
					// console.log('loaded ', data.loaded, '   total  -', data.total);
				}
				else if (data.type == 0) { }
				else if (data.ok && data.status && data.status == 200)
				{
					if (data.body)
					{
						this.uploading = false;

						// console.log(data.body);
						this.driverLicenceData = data.body;
						if (this.driverLicenceData.dob)
						{
							const parsedDate = new Date(this.driverLicenceData.dob);
							this.dobFormatted = DateHelper.getDateTimeAsFormat(parsedDate, Constants.DateFormatShort);
						}
						this.originalAddress = this.driverLicenceData.address;
						this.driverLicenceData.image = this.dataUrl;
						this.showLicenceData = true;
					}
					else
					{
						// console.log("Data ok, but no body");
					}
				}
				else
				{
					// console.log("Unexpected response");
				}
			},
			error: err =>
			{
				this.uploading = false;
				this.presentAlert("Upload Error", "An error occurred while uploading the licence. Please try again.");
				// loading.dismiss();
				this.handleError(err);
			}
		});
	}
	originalAddress: string;

	errorTitle: string;
	errorMessage: string;
	public handleError(error: any): void
	{
		if (this.dismissed) return;

		var dtoError;
		if (error.error && error.error.entityDataJson)
		{
			dtoError = JSON.parse(error.error.entityDataJson);
		}

		if (dtoError && dtoError.Message != null)
		{
			//api error
			var errorMessage = dtoError.Message;
			var statusCode = dtoError.StatusCode;

			switch (statusCode)
			{
				case 400:
					this.errorTitle = "Validation Error";
					this.errorMessage = errorMessage;
					return;
			}
		}

		this.userService.handleError(error);
	}

	async presentAlert(title: string, message: string, back: boolean = false)
	{
		this.userService.presentAlert(title, message, back);
	}

	dismissModal(save: boolean = false, relaunch: boolean = false)
	{
		this.dismissed = true;
		if (save)
		{
			// Dismiss the top modal returning some data object
			this.modalController.dismiss({
				data: this.driverLicenceData
			});
		}
		else
		{
			this.modalController.dismiss({
				relaunch: relaunch
			});
		}
	}
	scrollTop: boolean = true;
	ionScrollEvent(ev: any)
	{
		var scrollTop = (ev.detail.scrollTop == 0);
		if (this.scrollTop != scrollTop)
		{
			this.scrollTop = scrollTop;
		}
	}

}
